import type { ReactNode } from "react";
import { useEffect } from "react";
import ChevronLeftRounded from "@mui/icons-material/ChevronLeftRounded";
import ChevronRightRounded from "@mui/icons-material/ChevronRightRounded";
import { AppBar, Box, Toolbar } from "@mui/material";

import { NavigationScrollButton } from "../components/navigation-scroll-button";
import { NavigationTab } from "../components/navigation-tab";
import type { NavbarItem } from "../types";
import { useScrollControls } from "../hooks/use-scroll-controls";

const SCROLL_AMOUNT = 500;

export interface SecondaryNavbarProps {
	readonly navbarItems: NavbarItem[];
	readonly actions?: ReactNode;
}

export const SecondaryNavbar = ({ navbarItems, actions }: SecondaryNavbarProps) => {
	const { ref, scrollHidden, handleScrollClick, isAtScrollStart, isAtScrollEnd, onScroll, handleRefreshScroll } =
		useScrollControls();

	const navbarKey = navbarItems.map((item) => item.link).join(",");

	useEffect(() => {
		handleRefreshScroll();
	}, [navbarKey, handleRefreshScroll]);

	return (
		<AppBar position="static" sx={{ bgcolor: "background.paper", height: 64 }}>
			<Toolbar
				sx={{
					minHeight: "64px !important",
					height: 64,
					color: "text.primary",
					display: "grid",
					gridTemplateColumns: "1fr min-content",
					gap: actions ? 2 : 0,
				}}
				disableGutters={scrollHidden === false}
			>
				<Box
					sx={{
						height: "100%",
						display: "grid",
						gridTemplateColumns: scrollHidden ? "1fr" : "min-content 1fr min-content",
					}}
				>
					{scrollHidden === false && (
						<NavigationScrollButton disabled={isAtScrollStart()} onClick={() => handleScrollClick(-SCROLL_AMOUNT)}>
							<ChevronLeftRounded />
						</NavigationScrollButton>
					)}

					<Box
						ref={ref}
						onScroll={(event) => onScroll(event.currentTarget.scrollLeft)}
						sx={{
							display: "flex",
							overflowX: "auto",
							overflowY: "hidden",
							"::-webkit-scrollbar": {
								display: "none",
							},
						}}
						data-pendoid="scroll-menu__menu__navigation"
					>
						{navbarItems.map((tab) => (
							<NavigationTab
								key={tab.link}
								to={tab.link}
								data-cy-link={tab.link}
								data-pendoid={`scroll-menu__link__${tab.link.replace(/[^a-z0-9]/gi, "-").toLowerCase()}`}
							>
								{tab.text}
							</NavigationTab>
						))}
					</Box>

					{scrollHidden === false && (
						<NavigationScrollButton disabled={isAtScrollEnd()} onClick={() => handleScrollClick(SCROLL_AMOUNT)}>
							<ChevronRightRounded />
						</NavigationScrollButton>
					)}
				</Box>

				{actions}
			</Toolbar>
		</AppBar>
	);
};
